import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword
} from 'firebase/auth';
import { SESSION_DURATIONS } from '../config/authConfig';
import { auth, setAuthPersistence } from '../firebase/config';
import api from './api';

// Constantes para configuração de sessão (usando as configurações centralizadas)
const SESSION_CONFIG = {
  TOKEN_REFRESH_MARGIN: SESSION_DURATIONS.TOKEN_REFRESH_MARGIN,
  MAX_SESSION_DURATION: SESSION_DURATIONS.LONG_SESSION,
  EXTENDED_SESSION_DURATION: SESSION_DURATIONS.DEFAULT_SESSION,
  SHORT_SESSION_DURATION: SESSION_DURATIONS.SHORT_SESSION,
};

const checkEmailExists = async (email) => {
  try {
    const methods = await auth.fetchSignInMethodsForEmail(email);
    return methods.length > 0;
  } catch (error) {
    console.error('Erro ao verificar email:', error);
    return false;
  }
};

const register = async (email, password, name) => {
  try {
    const emailExists = await checkEmailExists(email);
    if (emailExists) {
      throw new Error('Este email já está registrado no Firebase');
    }

    const firebaseUser = await createUserWithEmailAndPassword(auth, email, password);

    try {
      const response = await api.post(`/auth/register`, {
        email,
        password,
        name
      });
      
      return {
        ...response.data,
        firebaseUser: firebaseUser.user
      };
    } catch (backendError) {
      await firebaseUser.user.delete();
      throw backendError;
    }
  } catch (error) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('Este email já está em uso no Firebase.');
    }
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Erro ao criar conta: ' + error.message);
  }
};

const login = async (email, password, rememberMe = true) => {
  try {
    // Configurar persistência antes do login
    await setAuthPersistence(rememberMe);
    
    const response = await api.post(`/auth/login`, {
      email,
      password
    });

    if (!response.data.token) {
      throw new Error('Token não recebido do servidor');
    }

    // Garantir que todos os dados necessários estejam presentes
    const sessionData = {
      ...response.data,
      id: response.data.id,
      token: response.data.token,
      refreshToken: response.data.refreshToken,
      rememberMe: rememberMe,
      lastActivity: Date.now(),
      sessionDuration: rememberMe ? SESSION_CONFIG.MAX_SESSION_DURATION : SESSION_CONFIG.SHORT_SESSION_DURATION
    };

    // Armazenar dados conforme preferência do usuário
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('user', JSON.stringify(sessionData));
    
    if (response.data.refreshToken) {
      storage.setItem('@MeuEsporte:refreshToken', response.data.refreshToken);
    }

    // Limpar do outro storage se existir
    const otherStorage = rememberMe ? sessionStorage : localStorage;
    otherStorage.removeItem('user');
    otherStorage.removeItem('@MeuEsporte:refreshToken');

    // Configurar token no header padrão do axios
    api.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;

    return sessionData;
  } catch (error) {
    console.error('Erro no login:', error);
    throw new Error(error.response?.data?.message || 'Erro ao realizar login');
  }
};

const logout = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('@MeuEsporte:refreshToken');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('@MeuEsporte:refreshToken');
  delete api.defaults.headers.common.Authorization;
  auth.signOut();
};

export const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem('@MeuEsporte:refreshToken') || 
                       sessionStorage.getItem('@MeuEsporte:refreshToken');
                       
    if (!refreshToken) throw new Error('Refresh token não encontrado');

    const response = await api.post('/auth/refresh', { refreshToken });
    
    if (!response.data.token) {
      throw new Error('Novo token não recebido');
    }

    // Atualizar token no storage apropriado
    const user = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || '{}');
    const storage = user.rememberMe ? localStorage : sessionStorage;
    
    const updatedUser = {
      ...user,
      token: response.data.token,
      lastActivity: Date.now()
    };
    
    storage.setItem('user', JSON.stringify(updatedUser));
    api.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;
    
    return response.data.token;
  } catch (error) {
    console.error('Erro ao renovar token:', error);
    throw new Error('Falha ao renovar o token');
  }
};

const isSessionValid = (sessionData) => {
  if (!sessionData || !sessionData.lastActivity) return false;
  
  const sessionAge = Date.now() - sessionData.lastActivity;
  const maxAge = sessionData.sessionDuration || SESSION_CONFIG.SHORT_SESSION_DURATION;
  
  return sessionAge < maxAge;
};

const verifyToken = async (token) => {
  if (!token) return false;
  
  try {
    // Para tokens do Firebase, verificar se ainda é válido
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        // Tentar obter um novo token do Firebase (isso verifica se ainda está autenticado)
        const freshToken = await currentUser.getIdToken(true);
        
        // Se conseguiu renovar, atualizar o token armazenado
        if (freshToken && freshToken !== token) {
          const userData = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || '{}');
          const storage = userData.rememberMe ? localStorage : sessionStorage;
          
          const updatedUser = {
            ...userData,
            token: freshToken,
            lastActivity: Date.now()
          };
          
          storage.setItem('user', JSON.stringify(updatedUser));
          api.defaults.headers.common.Authorization = `Bearer ${freshToken}`;
          
          console.log('[AuthService] Token do Firebase renovado automaticamente');
        }
        
        return true;
      } catch (error) {
        console.error('[AuthService] Erro ao renovar token do Firebase:', error);
        return false;
      }
    }

    // Fallback para verificação JWT tradicional
    const tokenData = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = tokenData.exp * 1000;
    
    // Verificar se o token vai expirar em breve
    if (Date.now() + SESSION_CONFIG.TOKEN_REFRESH_MARGIN >= expirationTime) {
      try {
        const newToken = await refreshToken();
        if (newToken) {
          return true;
        }
      } catch (error) {
        console.error('Erro ao renovar token:', error);
        return false;
      }
    }
    return true;
  } catch (error) {
    console.error('Erro ao verificar token:', error);
    
    // Se o token está malformado, tentar obter um novo do Firebase
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const freshToken = await currentUser.getIdToken(true);
        return !!freshToken;
      } catch {
        return false;
      }
    }
    
    return false;
  }
};

const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (!userData) return null;
    
    const user = JSON.parse(userData);
    
    // Verificar se a sessão ainda é válida
    if (!isSessionValid(user)) {
      logout();
      return null;
    }
    
    return user;
  } catch (error) {
    console.error('Erro ao recuperar usuário:', error);
    logout();
    return null;
  }
};

const updateLastActivity = () => {
  try {
    const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (!userData) return;
    
    const user = JSON.parse(userData);
    const storage = user.rememberMe ? localStorage : sessionStorage;
    
    storage.setItem('user', JSON.stringify({
      ...user,
      lastActivity: Date.now()
    }));
  } catch (error) {
    console.error('Erro ao atualizar atividade:', error);
  }
};

export const authService = {
  register,
  login,
  logout,
  getCurrentUser,
  updateLastActivity,
  refreshToken,
  verifyToken,
  isSessionValid,
  registerWithEmail: async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      switch (error.code) {
        case 'auth/email-already-in-use':
          throw new Error('Este email já está em uso.');
        case 'auth/invalid-email':
          throw new Error('Email inválido.');
        case 'auth/weak-password':
          throw new Error('A senha deve ter pelo menos 6 caracteres.');
        default:
          throw new Error('Erro ao criar conta.');
      }
    }
  },

  loginWithEmail: async (email, password, rememberMe = true) => {
    try {
      // Configurar persistência antes do login
      await setAuthPersistence(rememberMe);
      
      // Fazer login no Firebase primeiro
      const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password);
      
      // Obter o token do Firebase
      const firebaseToken = await firebaseUser.getIdToken(true);
      
      // Configurar o token para a próxima requisição
      api.defaults.headers.common.Authorization = `Bearer ${firebaseToken}`;

      // Fazer a autenticação no backend
      const response = await api.post('/auth/firebase-auth', {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        token: firebaseToken // Enviar o token para o backend validar
      });

      // Determinar duração da sessão baseada em rememberMe
      let sessionDuration;
      if (rememberMe) {
        sessionDuration = SESSION_CONFIG.MAX_SESSION_DURATION; // 30 dias
      } else {
        sessionDuration = SESSION_CONFIG.EXTENDED_SESSION_DURATION; // 7 dias (padrão melhorado)
      }

      // Criar sessão com os dados necessários
      const session = {
        ...response.data,
        id: firebaseUser.uid,
        email: firebaseUser.email,
        token: firebaseToken,
        displayName: response.data.name || email.split('@')[0],
        rememberMe: rememberMe,
        lastActivity: Date.now(),
        sessionDuration: sessionDuration,
        firebaseUser: {
          uid: firebaseUser.uid,
          email: firebaseUser.email
        }
      };

      // Armazenar conforme preferência do usuário
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('user', JSON.stringify(session));

      // Limpar do outro storage se existir
      const otherStorage = rememberMe ? sessionStorage : localStorage;
      otherStorage.removeItem('user');
      otherStorage.removeItem('@MeuEsporte:refreshToken');

      return session;
    } catch (error) {
      console.error('Erro no login com email:', error);
      if (error.code === 'auth/invalid-credential') {
        throw new Error('Email ou senha incorretos');
      }
      throw new Error(error.response?.data?.message || 'Erro ao realizar login');
    }
  },

  loginWithGoogle: async (rememberMe = true) => {
    try {
      // Configurar persistência antes do login
      await setAuthPersistence(rememberMe);
      
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const firebaseToken = await result.user.getIdToken(true);

      // Configurar token para próxima requisição
      api.defaults.headers.common.Authorization = `Bearer ${firebaseToken}`;

      // Fazer a autenticação no backend
      const response = await api.post('/auth/firebase-auth', {
        id: result.user.uid,
        email: result.user.email,
        token: firebaseToken,
        name: result.user.displayName
      });

      // Determinar duração da sessão baseada em rememberMe
      let sessionDuration;
      if (rememberMe) {
        sessionDuration = SESSION_CONFIG.MAX_SESSION_DURATION; // 30 dias
      } else {
        sessionDuration = SESSION_CONFIG.EXTENDED_SESSION_DURATION; // 7 dias (padrão melhorado)
      }

      // Criar sessão com dados completos
      const session = {
        ...response.data,
        id: result.user.uid,
        email: result.user.email,
        token: firebaseToken,
        displayName: result.user.displayName || response.data.name,
        rememberMe: rememberMe,
        lastActivity: Date.now(),
        sessionDuration: sessionDuration,
        firebaseUser: result.user
      };

      // Armazenar conforme preferência do usuário
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('user', JSON.stringify(session));

      // Limpar do outro storage se existir
      const otherStorage = rememberMe ? sessionStorage : localStorage;
      otherStorage.removeItem('user');
      otherStorage.removeItem('@MeuEsporte:refreshToken');

      return session;
    } catch (error) {
      console.error('Erro no login com Google:', error);
      throw new Error('Erro ao fazer login com Google.');
    }
  },

  resetPassword: async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw new Error('Erro ao enviar email de recuperação.');
    }
  },

  changePassword: async (newPassword) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Usuário não autenticado.');
      await updatePassword(user, newPassword);
    } catch (error) {
      throw new Error('Erro ao alterar senha.');
    }
  }
};